import { useEffect, useMemo, useState, type FormEvent } from 'react';
import {
  approveApInvoice,
  approvePaymentOut,
  createApInvoice,
  createPaymentOut,
  listAccountingPeriods,
  listApInvoices,
  listChartOfAccounts,
  listPaymentsOut,
  postApInvoiceJournal,
  postPaymentOutJournal,
} from '../../api/finance';
import { listSuppliers } from '../../api/masterData';
import { listGoodsReceipts } from '../../api/purchasing';
import { usePermissions } from '../../context/PermissionContext';
import { useBranch } from '../../context/BranchContext';
import {
  Button,
  ClickableRow,
  Drawer,
  DrawerField,
  EmptyState,
  ErrorNote,
  FieldLabel,
  LoadingNote,
  PageHeader,
  Select,
  StatusBadge,
  SummaryStrip,
  TextInput,
  tableStyle,
  tdStyle,
  thStyle,
} from '../../components/common';
import type { AccountingPeriod, ApInvoice, ChartOfAccount, GoodsReceipt, PaymentOut, Supplier } from '../../types/domain';

const currency = (n: number) => `Rp ${n.toLocaleString('id-ID')}`;

/**
 * Accounts Payable workspace (FASE 10, migration 0044): invoices + payments
 * in one workspace. Every invoice traces to a real, POSTED goods receipt
 * (source document) when gr_id is supplied — enforced server-side, no fake
 * invoice generation possible from this UI.
 */
export default function ApPage() {
  const { hasPermission } = usePermissions();
  const { activeBranchId } = useBranch();
  const [tab, setTab] = useState<'invoices' | 'payments'>('invoices');
  const [invoices, setInvoices] = useState<ApInvoice[]>([]);
  const [payments, setPayments] = useState<PaymentOut[]>([]);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [goodsReceipts, setGoodsReceipts] = useState<GoodsReceipt[]>([]);
  const [accountingPeriods, setAccountingPeriods] = useState<AccountingPeriod[]>([]);
  const [accounts, setAccounts] = useState<ChartOfAccount[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const canReadAp = hasPermission('ap_invoice', 'read');
  const canReadPayment = hasPermission('payment_out', 'read');

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const [invRows, payRows, supRows, grRows, apRows, coaRows] = await Promise.all([
        canReadAp ? listApInvoices(activeBranchId ?? undefined) : Promise.resolve([]),
        canReadPayment ? listPaymentsOut(activeBranchId ?? undefined) : Promise.resolve([]),
        listSuppliers(),
        listGoodsReceipts(activeBranchId ?? undefined),
        listAccountingPeriods(),
        listChartOfAccounts(),
      ]);
      setInvoices(invRows);
      setPayments(payRows);
      setSuppliers(supRows);
      setGoodsReceipts(grRows.filter((g) => g.status === 'POSTED'));
      setAccountingPeriods(apRows);
      setAccounts(coaRows);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeBranchId]);

  const supplierName = (id: string) => suppliers.find((s) => s.supplier_id === id)?.name ?? id;
  const invoiceNo = (id: string) => invoices.find((i) => i.invoice_id === id)?.invoice_no ?? id;

  return (
    <div>
      <PageHeader title="Accounts Payable" subtitle="Invoice supplier dan pembayaran keluar — selalu telusur ke dokumen sumber nyata. Migrasi 0044." />
      <div style={{ display: 'flex', gap: 4, marginBottom: 16, borderBottom: '1px solid var(--alex-border)' }}>
        {(['invoices', 'payments'] as const).map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            style={{
              border: 'none',
              background: 'none',
              padding: '10px 14px',
              fontSize: 'var(--alex-fs-caption)',
              fontWeight: tab === t ? 700 : 500,
              color: tab === t ? 'var(--alex-primary)' : 'var(--alex-text-secondary)',
              borderBottom: tab === t ? '2px solid var(--alex-primary)' : '2px solid transparent',
              cursor: 'pointer',
            }}
          >
            {t === 'invoices' ? 'Invoice AP' : 'Pembayaran'}
          </button>
        ))}
      </div>
      {loading && <LoadingNote />}
      {error && <ErrorNote text={error} />}
      {!loading && !error && tab === 'invoices' && (
        <InvoicesTab
          invoices={invoices}
          suppliers={suppliers}
          supplierName={supplierName}
          goodsReceipts={goodsReceipts}
          accountingPeriods={accountingPeriods}
          accounts={accounts}
          canCreate={hasPermission('ap_invoice', 'create')}
          canApprove={hasPermission('ap_invoice', 'approve')}
          canPost={hasPermission('ap_invoice', 'post')}
          activeBranchId={activeBranchId}
          onChanged={() => void load()}
        />
      )}
      {!loading && !error && tab === 'payments' && (
        <PaymentsTab
          payments={payments}
          invoices={invoices}
          invoiceNo={invoiceNo}
          accountingPeriods={accountingPeriods}
          accounts={accounts}
          canCreate={hasPermission('payment_out', 'create')}
          canApprove={hasPermission('payment_out', 'approve')}
          canPost={hasPermission('payment_out', 'post')}
          onChanged={() => void load()}
        />
      )}
    </div>
  );
}

function InvoicesTab({
  invoices,
  suppliers,
  supplierName,
  goodsReceipts,
  accountingPeriods,
  accounts,
  canCreate,
  canApprove,
  canPost,
  activeBranchId,
  onChanged,
}: {
  invoices: ApInvoice[];
  suppliers: Supplier[];
  supplierName: (id: string) => string;
  goodsReceipts: GoodsReceipt[];
  accountingPeriods: AccountingPeriod[];
  accounts: ChartOfAccount[];
  canCreate: boolean;
  canApprove: boolean;
  canPost: boolean;
  activeBranchId: string | null;
  onChanged: () => void;
}) {
  const [showForm, setShowForm] = useState(false);
  const [selected, setSelected] = useState<ApInvoice | null>(null);
  const [actionErr, setActionErr] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const openTotal = invoices.filter((i) => i.status !== 'PAID' && i.status !== 'VOID').reduce((s, i) => s + i.amount, 0);

  const handleApprove = async (id: string) => {
    setActionErr(null);
    setBusyId(id);
    try {
      await approveApInvoice(id);
      onChanged();
    } catch (e) {
      setActionErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div>
      <SummaryStrip items={[{ label: 'Total Invoice', value: invoices.length }, { label: 'Terbuka', value: currency(openTotal), tone: 'warning' as const }]} />
      {canCreate && (
        <Button variant="primary" onClick={() => setShowForm((v) => !v)} style={{ marginBottom: 16 }} disabled={!activeBranchId}>
          {showForm ? 'Batal' : '+ Invoice AP baru'}
        </Button>
      )}
      {showForm && canCreate && activeBranchId && (
        <NewInvoiceForm
          branchId={activeBranchId}
          suppliers={suppliers}
          goodsReceipts={goodsReceipts}
          onCreated={() => {
            setShowForm(false);
            onChanged();
          }}
        />
      )}
      {actionErr && <ErrorNote text={actionErr} />}
      {invoices.length === 0 && <EmptyState text="Belum ada invoice AP." />}
      {invoices.length > 0 && (
        <table style={tableStyle}>
          <thead>
            <tr>
              <th style={thStyle}>No. Invoice</th>
              <th style={thStyle}>Supplier</th>
              <th style={thStyle}>Jatuh Tempo</th>
              <th style={thStyle}>Jumlah</th>
              <th style={thStyle}>Status</th>
              <th style={thStyle}></th>
            </tr>
          </thead>
          <tbody>
            {invoices.map((inv) => (
              <ClickableRow key={inv.invoice_id} onClick={() => setSelected(inv)}>
                <td style={{ ...tdStyle, color: 'var(--alex-primary)', fontWeight: 600 }}>{inv.invoice_no}</td>
                <td style={tdStyle}>{supplierName(inv.supplier_id)}</td>
                <td style={tdStyle}>{inv.due_date}</td>
                <td style={tdStyle}>{currency(inv.amount)}</td>
                <td style={tdStyle}>
                  <StatusBadge status={inv.status} />
                </td>
                <td style={tdStyle}>
                  {inv.status === 'DRAFT' && canApprove && (
                    <Button
                      variant="secondary"
                      loading={busyId === inv.invoice_id}
                      onClick={(e) => {
                        e.stopPropagation();
                        void handleApprove(inv.invoice_id);
                      }}
                    >
                      Setujui
                    </Button>
                  )}
                </td>
              </ClickableRow>
            ))}
          </tbody>
        </table>
      )}
      <InvoiceDetailDrawer
        invoice={selected}
        open={!!selected}
        onClose={() => setSelected(null)}
        supplierName={supplierName}
        accountingPeriods={accountingPeriods}
        accounts={accounts}
        canPost={canPost}
        onChanged={onChanged}
      />
    </div>
  );
}

function NewInvoiceForm({
  branchId,
  suppliers,
  goodsReceipts,
  onCreated,
}: {
  branchId: string;
  suppliers: Supplier[];
  goodsReceipts: GoodsReceipt[];
  onCreated: () => void;
}) {
  const [supplierId, setSupplierId] = useState('');
  const [grId, setGrId] = useState('');
  const [invoiceNo, setInvoiceNo] = useState('');
  const [invoiceDate, setInvoiceDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [dueDate, setDueDate] = useState('');
  const [amount, setAmount] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const amt = Number(amount);
    if (!Number.isFinite(amt) || amt <= 0) {
      setError('Jumlah harus lebih dari 0.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await createApInvoice(branchId, supplierId, grId || null, invoiceNo.trim(), invoiceDate, dueDate, amt);
      onCreated();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="alex-card" style={{ marginBottom: 20, padding: 16, display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
      <div style={{ flex: 1, minWidth: 160 }}>
        <FieldLabel label="Supplier">
          <Select value={supplierId} onChange={(e) => setSupplierId(e.target.value)} required>
            <option value="" disabled>
              Pilih...
            </option>
            {suppliers.map((s) => (
              <option key={s.supplier_id} value={s.supplier_id}>
                {s.name}
              </option>
            ))}
          </Select>
        </FieldLabel>
      </div>
      <div style={{ width: 200 }}>
        <FieldLabel label="Goods Receipt (opsional)">
          <Select value={grId} onChange={(e) => setGrId(e.target.value)}>
            <option value="">— Tanpa GR —</option>
            {goodsReceipts.map((g) => (
              <option key={g.gr_id} value={g.gr_id}>
                {g.gr_number}
              </option>
            ))}
          </Select>
        </FieldLabel>
      </div>
      <div style={{ width: 150 }}>
        <FieldLabel label="No. Invoice">
          <TextInput value={invoiceNo} onChange={(e) => setInvoiceNo(e.target.value)} required />
        </FieldLabel>
      </div>
      <div style={{ width: 150 }}>
        <FieldLabel label="Tanggal Invoice">
          <TextInput type="date" value={invoiceDate} onChange={(e) => setInvoiceDate(e.target.value)} required />
        </FieldLabel>
      </div>
      <div style={{ width: 150 }}>
        <FieldLabel label="Jatuh Tempo">
          <TextInput type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} required />
        </FieldLabel>
      </div>
      <div style={{ width: 160 }}>
        <FieldLabel label="Jumlah (Rp)">
          <TextInput type="number" min="0" step="1" value={amount} onChange={(e) => setAmount(e.target.value)} required />
        </FieldLabel>
      </div>
      <Button type="submit" variant="primary" loading={submitting}>
        Simpan
      </Button>
      {error && <ErrorNote text={error} />}
    </form>
  );
}

function InvoiceDetailDrawer({
  invoice,
  open,
  onClose,
  supplierName,
  accountingPeriods,
  accounts,
  canPost,
  onChanged,
}: {
  invoice: ApInvoice | null;
  open: boolean;
  onClose: () => void;
  supplierName: (id: string) => string;
  accountingPeriods: AccountingPeriod[];
  accounts: ChartOfAccount[];
  canPost: boolean;
  onChanged: () => void;
}) {
  const [accountingPeriodId, setAccountingPeriodId] = useState('');
  const [expenseAccountId, setExpenseAccountId] = useState('');
  const [apAccountId, setApAccountId] = useState('');
  const [posting, setPosting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setAccountingPeriodId('');
    setExpenseAccountId('');
    setApAccountId('');
    setError(null);
  }, [invoice?.invoice_id]);

  if (!invoice) return null;

  const postableAccounts = accounts.filter((a) => a.is_postable && a.status === 'ACTIVE');
  const openAccountingPeriods = accountingPeriods.filter((p) => p.status === 'OPEN');

  const handlePost = async () => {
    setPosting(true);
    setError(null);
    try {
      await postApInvoiceJournal(invoice.invoice_id, accountingPeriodId, expenseAccountId, apAccountId);
      onChanged();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setPosting(false);
    }
  };

  return (
    <Drawer open={open} onClose={onClose} title={invoice.invoice_no}>
      <DrawerField label="Supplier" value={supplierName(invoice.supplier_id)} />
      <DrawerField label="Jumlah" value={currency(invoice.amount)} />
      <DrawerField label="Tanggal Invoice" value={invoice.invoice_date} />
      <DrawerField label="Jatuh Tempo" value={invoice.due_date} />
      <DrawerField label="Sumber (GR)" value={invoice.gr_id ?? 'Tanpa GR (invoice non-barang)'} />
      <DrawerField label="Status" value={<StatusBadge status={invoice.status} />} />

      {invoice.status === 'APPROVED' && canPost && (
        <div style={{ marginTop: 16, paddingTop: 12, borderTop: '1px solid var(--alex-border)' }}>
          <div style={{ fontWeight: 700, fontSize: 'var(--alex-fs-caption)', marginBottom: 8 }}>Posting ke Jurnal</div>
          <FieldLabel label="Periode Akuntansi (OPEN)">
            <Select value={accountingPeriodId} onChange={(e) => setAccountingPeriodId(e.target.value)}>
              <option value="">Pilih...</option>
              {openAccountingPeriods.map((p) => (
                <option key={p.period_id} value={p.period_id}>
                  {p.period_code}
                </option>
              ))}
            </Select>
          </FieldLabel>
          <div style={{ marginTop: 8 }}>
            <FieldLabel label="Akun Beban (Debit)">
              <Select value={expenseAccountId} onChange={(e) => setExpenseAccountId(e.target.value)}>
                <option value="">Pilih...</option>
                {postableAccounts.map((a) => (
                  <option key={a.account_id} value={a.account_id}>
                    {a.account_code} — {a.name}
                  </option>
                ))}
              </Select>
            </FieldLabel>
          </div>
          <div style={{ marginTop: 8 }}>
            <FieldLabel label="Akun Utang Usaha (Kredit)">
              <Select value={apAccountId} onChange={(e) => setApAccountId(e.target.value)}>
                <option value="">Pilih...</option>
                {postableAccounts.map((a) => (
                  <option key={a.account_id} value={a.account_id}>
                    {a.account_code} — {a.name}
                  </option>
                ))}
              </Select>
            </FieldLabel>
          </div>
          <Button variant="primary" loading={posting} style={{ marginTop: 12 }} onClick={() => void handlePost()} disabled={!accountingPeriodId || !expenseAccountId || !apAccountId}>
            Posting Jurnal
          </Button>
          {error && <ErrorNote text={error} />}
        </div>
      )}
    </Drawer>
  );
}

function PaymentsTab({
  payments,
  invoices,
  invoiceNo,
  accountingPeriods,
  accounts,
  canCreate,
  canApprove,
  canPost,
  onChanged,
}: {
  payments: PaymentOut[];
  invoices: ApInvoice[];
  invoiceNo: (id: string) => string;
  accountingPeriods: AccountingPeriod[];
  accounts: ChartOfAccount[];
  canCreate: boolean;
  canApprove: boolean;
  canPost: boolean;
  onChanged: () => void;
}) {
  const [showForm, setShowForm] = useState(false);
  const [selected, setSelected] = useState<PaymentOut | null>(null);
  const [actionErr, setActionErr] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const payableInvoices = useMemo(() => invoices.filter((i) => i.status === 'POSTED' || i.status === 'PAID'), [invoices]);

  const handleApprove = async (id: string) => {
    setActionErr(null);
    setBusyId(id);
    try {
      await approvePaymentOut(id);
      onChanged();
    } catch (e) {
      setActionErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div>
      {canCreate && (
        <Button variant="primary" onClick={() => setShowForm((v) => !v)} style={{ marginBottom: 16 }}>
          {showForm ? 'Batal' : '+ Pembayaran baru'}
        </Button>
      )}
      {showForm && canCreate && (
        <NewPaymentForm
          invoices={payableInvoices}
          invoiceNo={invoiceNo}
          onCreated={() => {
            setShowForm(false);
            onChanged();
          }}
        />
      )}
      {actionErr && <ErrorNote text={actionErr} />}
      {payments.length === 0 && <EmptyState text="Belum ada pembayaran." />}
      {payments.length > 0 && (
        <table style={tableStyle}>
          <thead>
            <tr>
              <th style={thStyle}>No. Pembayaran</th>
              <th style={thStyle}>Invoice</th>
              <th style={thStyle}>Tanggal</th>
              <th style={thStyle}>Jumlah</th>
              <th style={thStyle}>Metode</th>
              <th style={thStyle}>Status</th>
              <th style={thStyle}></th>
            </tr>
          </thead>
          <tbody>
            {payments.map((p) => (
              <ClickableRow key={p.payment_id} onClick={() => setSelected(p)}>
                <td style={{ ...tdStyle, color: 'var(--alex-primary)', fontWeight: 600 }}>{p.payment_no}</td>
                <td style={tdStyle}>{invoiceNo(p.ap_invoice_id)}</td>
                <td style={tdStyle}>{p.payment_date}</td>
                <td style={tdStyle}>{currency(p.amount)}</td>
                <td style={tdStyle}>{p.method}</td>
                <td style={tdStyle}>
                  <StatusBadge status={p.status} />
                </td>
                <td style={tdStyle}>
                  {p.status === 'DRAFT' && canApprove && (
                    <Button
                      variant="secondary"
                      loading={busyId === p.payment_id}
                      onClick={(e) => {
                        e.stopPropagation();
                        void handleApprove(p.payment_id);
                      }}
                    >
                      Setujui
                    </Button>
                  )}
                </td>
              </ClickableRow>
            ))}
          </tbody>
        </table>
      )}
      <PaymentDetailDrawer
        payment={selected}
        open={!!selected}
        onClose={() => setSelected(null)}
        invoiceNo={invoiceNo}
        accountingPeriods={accountingPeriods}
        accounts={accounts}
        canPost={canPost}
        onChanged={onChanged}
      />
    </div>
  );
}

function NewPaymentForm({ invoices, invoiceNo, onCreated }: { invoices: ApInvoice[]; invoiceNo: (id: string) => string; onCreated: () => void }) {
  const [apInvoiceId, setApInvoiceId] = useState('');
  const [paymentDate, setPaymentDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [amount, setAmount] = useState('');
  const [method, setMethod] = useState<PaymentOut['method']>('BANK_TRANSFER');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const amt = Number(amount);
    if (!Number.isFinite(amt) || amt <= 0) {
      setError('Jumlah harus lebih dari 0.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await createPaymentOut(apInvoiceId, paymentDate, amt, method);
      onCreated();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="alex-card" style={{ marginBottom: 20, padding: 16, display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
      <div style={{ flex: 1, minWidth: 180 }}>
        <FieldLabel label="Invoice (sudah POSTED)">
          <Select value={apInvoiceId} onChange={(e) => setApInvoiceId(e.target.value)} required>
            <option value="" disabled>
              Pilih...
            </option>
            {invoices.map((i) => (
              <option key={i.invoice_id} value={i.invoice_id}>
                {invoiceNo(i.invoice_id)} — {currency(i.amount)}
              </option>
            ))}
          </Select>
        </FieldLabel>
      </div>
      <div style={{ width: 150 }}>
        <FieldLabel label="Tanggal Bayar">
          <TextInput type="date" value={paymentDate} onChange={(e) => setPaymentDate(e.target.value)} required />
        </FieldLabel>
      </div>
      <div style={{ width: 160 }}>
        <FieldLabel label="Jumlah (Rp)">
          <TextInput type="number" min="0" step="1" value={amount} onChange={(e) => setAmount(e.target.value)} required />
        </FieldLabel>
      </div>
      <div style={{ width: 160 }}>
        <FieldLabel label="Metode">
          <Select value={method} onChange={(e) => setMethod(e.target.value as PaymentOut['method'])}>
            <option value="BANK_TRANSFER">Transfer Bank</option>
            <option value="CASH">Tunai</option>
            <option value="CHECK">Cek</option>
          </Select>
        </FieldLabel>
      </div>
      <Button type="submit" variant="primary" loading={submitting}>
        Simpan
      </Button>
      <div style={{ width: '100%', fontSize: 'var(--alex-fs-micro)', color: 'var(--alex-text-faint)' }}>
        Pembayaran yang melebihi sisa saldo invoice akan ditolak server.
      </div>
      {error && <ErrorNote text={error} />}
    </form>
  );
}

function PaymentDetailDrawer({
  payment,
  open,
  onClose,
  invoiceNo,
  accountingPeriods,
  accounts,
  canPost,
  onChanged,
}: {
  payment: PaymentOut | null;
  open: boolean;
  onClose: () => void;
  invoiceNo: (id: string) => string;
  accountingPeriods: AccountingPeriod[];
  accounts: ChartOfAccount[];
  canPost: boolean;
  onChanged: () => void;
}) {
  const [accountingPeriodId, setAccountingPeriodId] = useState('');
  const [apAccountId, setApAccountId] = useState('');
  const [cashAccountId, setCashAccountId] = useState('');
  const [posting, setPosting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setAccountingPeriodId('');
    setApAccountId('');
    setCashAccountId('');
    setError(null);
  }, [payment?.payment_id]);

  if (!payment) return null;

  const postableAccounts = accounts.filter((a) => a.is_postable && a.status === 'ACTIVE');
  const openAccountingPeriods = accountingPeriods.filter((p) => p.status === 'OPEN');

  const handlePost = async () => {
    setPosting(true);
    setError(null);
    try {
      await postPaymentOutJournal(payment.payment_id, accountingPeriodId, apAccountId, cashAccountId);
      onChanged();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setPosting(false);
    }
  };

  return (
    <Drawer open={open} onClose={onClose} title={payment.payment_no}>
      <DrawerField label="Invoice" value={invoiceNo(payment.ap_invoice_id)} />
      <DrawerField label="Jumlah" value={currency(payment.amount)} />
      <DrawerField label="Tanggal" value={payment.payment_date} />
      <DrawerField label="Metode" value={payment.method} />
      <DrawerField label="Status" value={<StatusBadge status={payment.status} />} />

      {payment.status === 'APPROVED' && canPost && (
        <div style={{ marginTop: 16, paddingTop: 12, borderTop: '1px solid var(--alex-border)' }}>
          <div style={{ fontWeight: 700, fontSize: 'var(--alex-fs-caption)', marginBottom: 8 }}>Posting ke Jurnal</div>
          <FieldLabel label="Periode Akuntansi (OPEN)">
            <Select value={accountingPeriodId} onChange={(e) => setAccountingPeriodId(e.target.value)}>
              <option value="">Pilih...</option>
              {openAccountingPeriods.map((p) => (
                <option key={p.period_id} value={p.period_id}>
                  {p.period_code}
                </option>
              ))}
            </Select>
          </FieldLabel>
          <div style={{ marginTop: 8 }}>
            <FieldLabel label="Akun Utang Usaha (Debit)">
              <Select value={apAccountId} onChange={(e) => setApAccountId(e.target.value)}>
                <option value="">Pilih...</option>
                {postableAccounts.map((a) => (
                  <option key={a.account_id} value={a.account_id}>
                    {a.account_code} — {a.name}
                  </option>
                ))}
              </Select>
            </FieldLabel>
          </div>
          <div style={{ marginTop: 8 }}>
            <FieldLabel label="Akun Kas/Bank (Kredit)">
              <Select value={cashAccountId} onChange={(e) => setCashAccountId(e.target.value)}>
                <option value="">Pilih...</option>
                {postableAccounts.map((a) => (
                  <option key={a.account_id} value={a.account_id}>
                    {a.account_code} — {a.name}
                  </option>
                ))}
              </Select>
            </FieldLabel>
          </div>
          <Button variant="primary" loading={posting} style={{ marginTop: 12 }} onClick={() => void handlePost()} disabled={!accountingPeriodId || !apAccountId || !cashAccountId}>
            Posting Jurnal
          </Button>
          {error && <ErrorNote text={error} />}
        </div>
      )}
    </Drawer>
  );
}
